import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { WEBSOCKET_URLS } from '@/shared/consts';
import {
  Badge,
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { dispatch } from '@/redux';
import { Room, User } from '../types';
import { useHttpMutation } from '@/api';
import { API_URLS } from '../consts';
import { BhabiStorage, soundEffect } from '@/shared/lib';
import { getUser } from '@/redux/reducers';
import { IN_PLAY_ROUTE } from '../in-play';
import { useNavigate } from 'react-router-dom';

import { ActionButtonStyled } from './styled';
import { useWebsocket } from '../use-websocket';
import { NotEnoughCoins, useNotEnoughCoins } from '@/shared/components';
import { Bell } from 'lucide-react';

export const InviteNotifierWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(1),
  left: theme.spacing(1),
  zIndex: 1000,
  cursor: 'pointer',
}));

export declare type InviteNotificationResponse = {
  id: string;
  userID: string;
  fromUser: User;
  to: string;
  toUser: User;
  roomID: string;
  room: Room;
  status: 'pending' | 'rejoin';
  createdAt: string;
  updatedAt: string;
};

const menuId = 'primary-notification-menu';

export const InviteNotifier: FC = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isInviteMenuOpen = Boolean(anchorEl);

  const { mutate: rejectInvite, isLoading: isRejectInviteLoading } =
    useHttpMutation<unknown, { inviteID: string }>(
      API_URLS.REJECT_INVITE,
      'POST'
    );

  const { mutate: joinRoom, isLoading: isJoinRoomLoading } = useHttpMutation<
    { token: string },
    { roomID: string }
  >(API_URLS.JOIN_ROOM, 'POST');

  const { mutate: reJoinRoom, isLoading: isRejoinRoomLoading } =
    useHttpMutation<{ token: string }, { roomID: string }>(
      API_URLS.RE_JOIN_ROOM,
      'POST'
    );

  const nav = useNavigate();
  const { sendMessage, socketResponse: lastJsonMessage } = useWebsocket<{
    invites: InviteNotificationResponse[];
  }>(WEBSOCKET_URLS().INVITE_NOTIFICATION);

  useEffect(() => {
    const interval = setInterval(() => {
      sendMessage('Ping');
    }, 2000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(prev => (!prev ? event.currentTarget : null));
  }, []);

  const rejectInviteHandler = useCallback(
    (inviteID: string) => {
      rejectInvite(
        { inviteID },
        {
          onSuccess: () => {
            sendMessage('update');
          },
        }
      );
    },
    [rejectInvite, sendMessage]
  );

  const {
    checkIfEnoughCoins,
    hasEnoughCoins,
    showCoinsMessage,
    setShowCoinsMessage,
  } = useNotEnoughCoins();

  const acceptInviteHandler = useCallback(
    (roomID: string) => {
      if (!hasEnoughCoins) {
        checkIfEnoughCoins();
        return;
      }

      joinRoom(
        { roomID },
        {
          onSuccess: res => {
            BhabiStorage.setToken(res.token);
            dispatch(getUser(res.token));
            nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
          },
        }
      );
    },
    [joinRoom, nav, hasEnoughCoins, checkIfEnoughCoins]
  );

  const rejoinInviteHandler = useCallback(
    (roomID: string) => {
      reJoinRoom(
        { roomID },
        {
          onSuccess: res => {
            BhabiStorage.setToken(res.token);
            dispatch(getUser(res.token));
            nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
          },
        }
      );
    },
    [nav, reJoinRoom]
  );

  const selector = useCallback(
    (n: InviteNotificationResponse) => ({
      pending: {
        title: (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: theme.palette.text.primary }}
          >
            {`${n.fromUser.name} has invited you to join a room`}
          </Typography>
        ),
        actionButtons: (
          <>
            <ActionButtonStyled
              variant="contained"
              color="primary"
              size="small"
              disabled={isJoinRoomLoading || n.room.isFull}
              onClick={() => acceptInviteHandler(n.roomID)}
              sx={{
                minWidth: 80,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              Accept
            </ActionButtonStyled>
            <ActionButtonStyled
              variant="outlined"
              color="error"
              size="small"
              disabled={isRejectInviteLoading}
              onClick={() => rejectInviteHandler(n.id)}
              sx={{
                minWidth: 80,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              Reject
            </ActionButtonStyled>
          </>
        ),
      },
      rejoin: {
        title: (
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: theme.palette.text.primary }}
          >
            {`${n.fromUser.name} has rejoined a room`}
          </Typography>
        ),
        actionButtons: (
          <>
            <ActionButtonStyled
              variant="contained"
              color="primary"
              size="small"
              disabled={isRejoinRoomLoading}
              onClick={() => rejoinInviteHandler(n.roomID)}
              sx={{
                minWidth: 80,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              Rejoin
            </ActionButtonStyled>
            <ActionButtonStyled
              variant="outlined"
              color="error"
              size="small"
              disabled={isRejectInviteLoading}
              onClick={() => rejectInviteHandler(n.id)}
              sx={{
                minWidth: 80,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'capitalize',
              }}
            >
              Reject
            </ActionButtonStyled>
          </>
        ),
      },
    }),
    [
      acceptInviteHandler,
      isJoinRoomLoading,
      isRejectInviteLoading,
      isRejoinRoomLoading,
      rejectInviteHandler,
      rejoinInviteHandler,
      theme.palette.text.primary,
    ]
  );

  const lastNotification = useRef<number | null>(null);
  useEffect(() => {
    if (!lastJsonMessage?.invites?.length) return;

    if (!lastNotification.current) {
      soundEffect.playSound(soundEffect.notification);
      lastNotification.current = lastJsonMessage?.invites?.length;
    }
  }, [lastJsonMessage?.invites?.length]);

  return (
    <>
      <NotEnoughCoins
        showCoinsMessage={showCoinsMessage}
        setShowCoinsMessage={setShowCoinsMessage}
      />
      <IconButton aria-label="Game invitations" sx={{ width: 44, height: 44 }} onClick={handleMenuOpen}>
        <Badge
          badgeContent={lastJsonMessage?.invites?.length}
          color="success"
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.success.main,
              boxShadow: `0 0 0 1.5px ${theme.palette.background.paper}`,
              // Compact pill so it doesn't dwarf the 20px bell icon.
              height: 14,
              minWidth: 14,
              padding: '0 4px',
              fontSize: '0.625rem',
              lineHeight: 1,
              fontWeight: 700,
              top: 2,
              right: 2,
            },
          }}
        >
          <Bell size={20} strokeWidth={1.75} />
        </Badge>
      </IconButton>
      <Popper
        open={isInviteMenuOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        id={menuId}
        hidden={!lastJsonMessage?.invites?.length}
        sx={{ zIndex: 3500 }}
      >
        <ClickAwayListener
          // Treat the toggle IconButton as the popper's owner so clicking it
          // doesn't fire close-then-reopen. Without mouseEvent='onMouseDown'
          // Radix-style action buttons inside the menu still work because
          // they don't propagate to document mousedown before their handler.
          mouseEvent="onMouseDown"
          onClickAway={event => {
            const t = event.target as HTMLElement | null;
            if (t && anchorEl && anchorEl.contains(t)) return;
            setAnchorEl(null);
          }}
        >
        <Paper
          elevation={8}
          sx={{
            mt: 1,
            ml: 0.5,
            maxWidth: 420,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? theme.palette.background.default
                  : theme.palette.primary.light,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                letterSpacing: 0.5,
              }}
            >
              🎮 Game Invitations ({lastJsonMessage?.invites?.length || 0})
            </Typography>
          </Box>

          {/* Notifications List */}
          <Box
            sx={{
              maxHeight: 'calc(100vh - 200px)',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.divider,
                borderRadius: '3px',
                '&:hover': {
                  background: theme.palette.text.disabled,
                },
              },
            }}
          >
            {lastJsonMessage?.invites?.map((n, i) => (
              <Box
                key={[n.fromUser.email, i].join('-')}
                sx={{
                  px: 1.75,
                  py: 1.25,
                  borderBottom:
                    i < (lastJsonMessage?.invites?.length ?? 0) - 1
                      ? `1px solid ${theme.palette.divider}`
                      : 'none',
                  backgroundColor: theme.palette.background.paper,
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                {/* Title Section */}
                <Box sx={{ mb: 0.75 }}>{selector(n)[n.status].title}</Box>

                {/* Room Info Section */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    px: 1,
                    py: 0.5,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                      }}
                    >
                      Players: {n.room.numberOfPlayers}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {n.room.isFull ? (
                      <>
                        <CloseIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.error.main,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.error.main,
                            fontWeight: 600,
                          }}
                        >
                          Full
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon
                          sx={{
                            fontSize: 18,
                            color: theme.palette.success.main,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.success.main,
                            fontWeight: 600,
                          }}
                        >
                          Open
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'flex-end',
                  }}
                >
                  {selector(n)[n.status].actionButtons}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
